import { z } from 'zod';
import { resourceIdSchema, resourceRecoverySchema } from '../content/resource.js';
import { contentIdSchema, pathSchema } from './content-id.js';
/**
 * Счётчик ресурса на листе (D56).
 *
 * Определение приходит из записи контента или из шага журнала (свой
 * счётчик), максимум считает движок, а потраченное — свёртка журнала.
 * Клиенту всё это приезжает одной строкой: сколько осталось, сколько
 * всего и откуда взялось «всего».
 */
export const sheetResourceSchema = z.strictObject({
    id: resourceIdSchema,
    name: z.string().min(1),
    /** Сколько осталось: максимум минус потраченное, не ниже нуля. */
    current: z.number().int().nonnegative(),
    max: z.number().int().nonnegative(),
    recovery: resourceRecoverySchema,
    /**
     * Запись, объявившая счётчик; `null` — свой счётчик игрока.
     * По нему лист ставит ресурс к его особенности, а не в общий
     * список в стороне.
     */
    source: contentIdSchema.nullable(),
    /** Ключ особенности внутри записи; `null` — счётчик самой записи. */
    feature: z.string().nullable(),
    /**
     * Разбор максимума (D5). У ссылочного максимума это провенанс
     * той величины, на которую он ссылается: «ярости 4 = 2 (Варвар 1)
     * + 1 (Варвар 3) + 1 (Варвар 6)». У числового — пусто: объяснять
     * там нечего, число написано в записи.
     */
    maxFrom: pathSchema.nullable(),
});
/**
 * Действие со счётчиком (D3): трата, возврат, свой счётчик и его
 * удаление.
 *
 * Трата и возврат — команды, как урон и лечение: они меняют состояние,
 * а не величину листа, и пишутся шагом журнала.
 */
export const resourceActionSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('spend'),
        resource: resourceIdSchema,
        amount: z.number().int().positive(),
    }),
    z.strictObject({
        kind: z.literal('restore'),
        resource: resourceIdSchema,
        amount: z.number().int().positive(),
    }),
    /**
     * Свой счётчик (D56): имя, максимум числом, восстановление.
     * Записью контента он не становится — это решение журнала,
     * и живёт оно на одном персонаже.
     */
    z.strictObject({
        kind: z.literal('add'),
        name: z.string().min(1).max(60),
        max: z.number().int().positive().max(999),
        recovery: z.enum(['none', 'long_rest', 'short_rest']),
    }),
    z.strictObject({
        kind: z.literal('remove'),
        resource: resourceIdSchema,
    }),
]);
export const resourceProblemSchema = z.strictObject({
    code: z.enum([
        /** Счётчика с таким идентификатором на листе нет. */
        'no-such-resource',
        /** Тратить нечего: уже ноль. */
        'nothing-left',
        /** Возвращать некуда: уже максимум. */
        'already-full',
        /**
         * Счётчик с таким именем уже есть. Имя, а не ключ: ключ
         * человек не видит, а два «Настойки» на одном листе он
         * не различит.
         */
        'name-taken',
        /** Убрать можно только свой счётчик, а не тот, что дала запись. */
        'not-your-own',
    ]),
    /** Чего касается отказ: идентификатор счётчика или ключ. */
    subject: z.string().nullable(),
    message: z.string().min(1),
});
//# sourceMappingURL=resources.js.map